import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Crown, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';

const API = `${window.location.origin}/api`;

export const LifetimePricingTools = () => {
  const { getAuthHeader } = useAuth();
  const headers = useCallback(() => ({ headers: getAuthHeader() }), [getAuthHeader]);
  const [pricing, setPricing] = useState(null);
  const [subs, setSubs] = useState(null);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState('');
  const [results, setResults] = useState(null);

  const load = useCallback(async () => {
    const p = await axios.get(`${API}/pricing`); setPricing(p.data);
  }, []);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const loadSubs = async () => {
    setBusy('subs');
    try { const r = await axios.get(`${API}/admin/monthly-subscribers`, headers()); setSubs(r.data.subscriptions); setSelected([]); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not load Stripe subscriptions'); }
    setBusy('');
  };
  const setOverride = async (mode) => {
    setBusy('override');
    try { const r = await axios.put(`${API}/admin/pricing/override`, { mode }, headers()); setPricing(r.data); toast.success(`Pricing mode: ${mode}`); }
    catch { toast.error('Could not update pricing mode'); }
    setBusy('');
  };
  const runUpgrade = async () => {
    if (!window.confirm(`Grant FREE LIFETIME access to ${selected.length} subscriber(s) and cancel their Stripe billing? No refunds are issued.`)) return;
    setBusy('upgrade');
    try { const r = await axios.post(`${API}/admin/lifetime-upgrade`, { emails: selected }, headers()); setResults(r.data.results); toast.success('Done'); await loadSubs(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Upgrade failed'); }
    setBusy('');
  };
  const toggle = (email) => setSelected((s) => (s.includes(email) ? s.filter((x) => x !== email) : [...s, email]));

  return (
    <div className="space-y-6" data-testid="lifetime-tools-panel">
      <div className="glass-card rounded-2xl p-6 border-2 border-[#87A6A7]/30">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-[#87A6A7]/10 flex items-center justify-center"><Crown className="h-5 w-5 text-[#87A6A7]" /></div>
          <div>
            <h2 className="font-serif text-xl text-[#726158]">Lifetime Pricing</h2>
            <p className="text-[#AA9283] text-sm">Auto: $67 Oct 19–Nov 1 ET, $97 from Nov 2. Override only if you need to force a price.</p>
          </div>
        </div>
        {pricing && (
          <p className="text-sm text-[#726158] mb-3" data-testid="pricing-status">
            Live now: <strong>${pricing.lifetime_price}</strong> lifetime · <strong>${pricing.upgrade_price}</strong> trial upgrade · mode <strong>{pricing.override_mode}</strong>{pricing.is_early_access ? ' · early access ON' : ' · regular pricing'}
          </p>
        )}
        <div className="flex flex-wrap gap-2">
          {['auto', 'early', 'regular'].map((m) => (
            <Button key={m} size="sm" variant={pricing?.override_mode === m ? 'default' : 'outline'} disabled={!!busy} onClick={() => setOverride(m)}
              className={pricing?.override_mode === m ? 'bg-[#726158] text-white' : ''} data-testid={`pricing-mode-${m}`}>
              {m === 'auto' ? 'Automatic (date-based)' : m === 'early' ? 'Force $67 early access' : 'Force $97 regular'}
            </Button>
          ))}
        </div>
      </div>

      <div className="glass-card rounded-2xl p-6">
        <h3 className="font-medium text-[#726158]">Former monthly subscribers → free lifetime</h3>
        <p className="text-xs text-[#AA9283] mt-1 mb-3">Reads live Stripe subscriptions. For each person you tick: account set to lifetime first, then Stripe billing cancelled. No refunds.</p>
        <Button variant="outline" onClick={loadSubs} disabled={!!busy} data-testid="load-monthly-subs">
          {busy === 'subs' ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />} Load active monthly subscriptions
        </Button>
        {subs && (
          <div className="mt-4 space-y-2" data-testid="monthly-subs-list">
            {subs.length === 0 && <p className="text-sm text-[#726158]">No active monthly subscriptions in Stripe.</p>}
            {subs.map((s) => (
              <label key={s.subscription_id} className="flex flex-wrap items-center gap-3 text-sm text-[#726158] bg-white/60 rounded-lg px-3 py-2 border border-[#EAD8C2]">
                <input type="checkbox" checked={selected.includes(s.email)} onChange={() => toggle(s.email)} data-testid={`sub-check-${s.email}`} />
                <span className="font-medium">{s.name || '—'}</span><span>{s.email}</span>
                <span className="text-xs text-[#AA9283]">${s.amount}/mo · {s.status}{s.cancel_at_period_end ? ' · cancelling' : ''}</span>
                <span className="text-xs ml-auto">{s.in_db ? (s.is_lifetime ? 'lifetime ✓' : `account: ${s.accessType}`) : 'no account found'}</span>
              </label>
            ))}
            {subs.length > 0 && (
              <Button onClick={runUpgrade} disabled={busy || selected.length === 0} className="bg-[#726158] hover:bg-[#5e4f47] text-white" data-testid="run-lifetime-upgrade">
                {busy === 'upgrade' ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Crown className="h-4 w-4 mr-1" />} Grant lifetime & stop billing for {selected.length}
              </Button>
            )}
            {results && (
              <ul className="text-xs text-[#726158] space-y-1" data-testid="lifetime-upgrade-results">
                {results.map((r) => <li key={r.email}>{r.user_updated ? '✓ account' : '✗ no account'} · {r.subscriptions_cancelled.length} subscription(s) cancelled · {r.email}{r.error ? ` — ${r.error}` : ''}</li>)}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
